import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

/**
 * HSN validation + exceptions.
 *
 * HSN codes carry no checksum, so "is this valid" is two questions: is it
 * SHAPED like a code (length, digits, SAC prefix), and does it EXIST in the
 * master list. The backend answers both; this service just relays.
 */

export type HsnKind = 'GOODS' | 'SERVICE';
export type HsnExceptionKind = 'STRUCTURE' | 'NOT_IN_MASTER';

export interface HsnValidationResult {
  ok: boolean;
  normalized: string;
  exists?: boolean;
  errors: string[];
  warnings: string[];
  /** Restored form when a digit looks lost. Never applied automatically. */
  suggestion?: string;
  master?: { code: string; description: string };
}

export interface HsnRepairSuggestion {
  code: string;
  description: string;
}

export interface HsnExceptionRow {
  hsnCode: string;
  normalized: string;
  kind: HsnExceptionKind;
  reason: string;
  message: string;
  productCount: number;
  suggestions: HsnRepairSuggestion[];
  /** Exactly one repair exists in the master — safe to offer as one click. */
  confident: boolean;
  sampleProducts: Array<{ id: string; sku: string; name: string }>;
}

export interface HsnExceptionSummary {
  activeProducts: number;
  distinctCodes: number;
  affectedProducts: number;
  affectedCodes: number;
  percentAffected: number;
  masterLoaded: boolean;
  byReason: Record<string, { codes: number; products: number }>;
}

export interface HsnExceptionReport {
  summary: HsnExceptionSummary;
  rows: HsnExceptionRow[];
}

export interface HsnMasterStats {
  total: number;
  goods: number;
  services: number;
  retired: number;
  lastImport: {
    source: string;
    sourceVersion: string | null;
    finishedAt: string | null;
  } | null;
}

@Injectable({ providedIn: 'root' })
export class HsnService {
  private readonly http = inject(HttpClient);
  private readonly base = environment.hsn;

  /** Validate one code — structure plus existence. Used on the add-product form. */
  validate(
    code: string,
    options: { minDigits?: number; expectKind?: HsnKind } = {},
  ): Observable<HsnValidationResult> {
    return this.http.post<HsnValidationResult>(`${this.base}/validate`, {
      code,
      ...options,
    });
  }

  /** Every product with an HSN problem, for the logged-in client. */
  exceptions(options: { minDigits?: number } = {}): Observable<HsnExceptionReport> {
    let params = new HttpParams();
    if (options.minDigits) {
      params = params.set('minDigits', String(options.minDigits));
    }
    return this.http.get<HsnExceptionReport>(`${this.base}/exceptions`, {
      params,
    });
  }

  /** Full product list behind one exception row. */
  productsForCode(
    hsnCode: string,
    limit = 200,
  ): Observable<Array<{ id: string; sku: string; name: string }>> {
    const params = new HttpParams()
      .set('hsnCode', hsnCode)
      .set('limit', String(limit));
    return this.http.get<Array<{ id: string; sku: string; name: string }>>(
      `${this.base}/exceptions/products`,
      { params },
    );
  }

  /** Replace one code across every product carrying it. */
  applyCorrection(
    fromCode: string,
    toCode: string,
  ): Observable<{ updated: number; normalized: string; warnings: string[] }> {
    return this.http.post<{
      updated: number;
      normalized: string;
      warnings: string[];
    }>(`${this.base}/exceptions/apply-correction`, { fromCode, toCode });
  }

  /** Type-ahead over the master — code prefix or description text. */
  search(
    q: string,
    limit = 20,
  ): Observable<Array<{ code: string; description: string }>> {
    const params = new HttpParams().set('q', q).set('limit', String(limit));
    return this.http.get<Array<{ code: string; description: string }>>(
      `${this.base}/search`,
      { params },
    );
  }

  /** Master coverage — drives the "master not loaded" banner. */
  stats(): Observable<HsnMasterStats> {
    return this.http.get<HsnMasterStats>(`${this.base}/stats`);
  }
}
